import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { z } from 'zod';
import { URL_EMPLOYEE } from '../../router/constants';
import useAnalytics from '../../hooks/useAnalytics';
import PermissionsService, { PERMISSION_PERSON } from '../../Services/PermissionsService';
import { requirePermission } from '../../router/RouteGuardsService';
import Form from '../../components/Employee/Form';
import Detail from '../../components/Employee/Detail';
import { EMPLOYEE_TABLE_NAME } from '../../components/Employee/constants';
import { ICmdTableFilter } from '../../components/CmdTable/types';
import {
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../components/CmdTable/CmdTableState';
import { selectedCustomersAtom } from '../../store/jotai/customers';
import { TABLE_PERSON } from '../../Services/TableService';

const editEmployeeSearchSchema = z.object({
  customerId: z.coerce.number().optional(),
});

export const Route = createFileRoute('/_authenticated/employee/edit/$id')({
  validateSearch: editEmployeeSearchSchema,
  beforeLoad: async ({ context }) => {
    await requirePermission(PERMISSION_PERSON, ['read'], '/unauthorized', context);
    return {
      ...context,
      documentTitle: {
        main: 'employee.edit',
        suffix: 'menu.employeeSettings',
      },
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: EditEmployeeRoute,
});

function EditEmployeeRoute() {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const selectedCustomers = useAtomValue(selectedCustomersAtom);
  const filterForRequest = useAtomValue<ICmdTableFilter>(
    filterForRequestAtomFamily(EMPLOYEE_TABLE_NAME)
  );
  const sortFilter = useAtomValue<{ [key: string]: string }>(
    sortFilterAtomFamily(EMPLOYEE_TABLE_NAME)
  );

  const handleClose = useCallback(() => {
    navigate({ to: URL_EMPLOYEE });
    trackEvent(['employees', 'click', 'Employees close']);
  }, [navigate, trackEvent]);

  const { id } = Route.useParams();
  const search = Route.useSearch();
  const employeeId = Number(id);
  const customerId = search.customerId ?? null;

  const updateTable = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [EMPLOYEE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [filterForRequest, queryClient, selectedCustomers, sortFilter]);

  if (PermissionsService.hasAccess(TABLE_PERSON, ['update'], customerId)) {
    return (
      <Form closeCallback={handleClose} editId={employeeId} isEdited updateTable={updateTable} />
    );
  }

  if (PermissionsService.hasAccess(TABLE_PERSON, ['read'], customerId)) {
    return <Detail id={employeeId} closeCallback={handleClose} />;
  }

  return null;
}
